import { Injector } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import _ from 'lodash';

import { AuthService } from '../auth/auth.service';
import { APIClientService } from '../common/api-client.service';
import { AppConfigurationService } from '../common/app-configuration.service';
import { EventsManagerService } from '../common/events-manager.service';
import { HttpInterceptor } from '../common/http-interceptor.service';
import { DatabaseService } from '../storage/database.service';
import { IStorable } from '../storage/storable.interface';
import { ISmartenitConfig } from '../smartenit-config.interface';
import { IRequestOptions } from './request-options.interface';

export class ResourceReaderService extends APIClientService {
  protected _data: any = { version: '0.0.0' };
  protected _loaded: boolean = false;
  protected _onLoaded: Subject<any> = new Subject<any>();

  get version(): string {
    return this._data.version;
  }

  get data(): any {
    return this._data;
  }

  set data(value: any) {
    this._data = value;
  }

  get onLoaded(): Observable<any> {
    return this._onLoaded.asObservable();
  }

  get isLoaded(): boolean {
    return this._loaded;
  }

  constructor(
    resource: string,
    http: HttpInterceptor,
    authService: AuthService,
    public databaseService: DatabaseService,
    public eventsService: EventsManagerService,
    public AppConfiguration: AppConfigurationService,
    public injector: Injector,
    private dbKey: string,
    private resourceName: string
  ) {
    super(
      resource,
      http,
      authService,
      eventsService,
      AppConfiguration,
      injector
    );
  }

  protected loadLocalVersion(): Observable<any> {
    return this.databaseService
      .getCollection(this.dbKey)
      .getById(this.dbKey)
      .flatMap((localVersion) => {
        if (localVersion != null) {
          this._data = localVersion;
          return Observable.of(true);
        } else {
          return this.buildInitialData();
        }
      });
  }

  protected buildInitialData(): Observable<any> {
    return Observable.of(true);
  }

  protected storeLocalVersion(remoteData: any) {
    const remoteVersion = remoteData.version;
    const localVersion = this._data.version;
    if (remoteVersion && localVersion) {
      if (this.versionCompare(remoteVersion, localVersion) >= 0) {
        this.processData(remoteData);

        this.databaseService
          .getCollection(this.dbKey)
          .save(this.dbKey, remoteData)
          .subscribe((storeResult) => {
            console.log(
              `${this.resourceName}: current (` +
                localVersion +
                ') remote (' +
                remoteVersion +
                ')'
            );
            this._loaded = true;
            this._onLoaded.next(true);
          });
      } else {
        console.log(
          `${this.resourceName}: current (` +
            localVersion +
            ') remote (' +
            remoteVersion +
            ')'
        );
        this._loaded = true;
        this._onLoaded.next(true);
      }
    }
  }

  protected processData(remoteData: any) {}

  private versionCompare(v1: string, v2: string) {
    const v1parts = v1.split('.').map(Number);
    const v2parts = v2.split('.').map(Number);

    if (!this.validateParts(v1parts) || !this.validateParts(v2parts)) {
      return NaN;
    }

    for (let i = 0; i < v1parts.length; ++i) {
      if (v2parts.length === i) {
        return 1;
      }

      if (v1parts[i] === v2parts[i]) {
        continue;
      }
      if (v1parts[i] > v2parts[i]) {
        return 1;
      }
      return -1;
    }

    if (v1parts.length != v2parts.length) {
      return -1;
    }

    return 0;
  }

  private validateParts(parts: any) {
    for (let i = 0; i < parts.length; ++i) {
      if (!this.isPositiveInteger(parts[i])) {
        return false;
      }
    }
    return true;
  }

  private isPositiveInteger(x: any) {
    return /^\d+$/.test(x);
  }
}
